import axios from 'axios'

import { API_BASE_URL } from '@/common/config/api'

import { prepareAuthHeaders } from './baseQueryWithReauth'

// The server holds the request until the malware scan answers (up to 60 s) on top of the transfer
// itself. The app's usual 30 s timeout would abandon uploads that are about to succeed.
export const UPLOAD_TIMEOUT_MS = 150_000

/**
 * Uploads one file to an attachments endpoint. This uses axios rather than RTK Query because fetch
 * cannot report upload progress, and the user needs to see "uploading" turn into "checking".
 *
 * Rejects with `{ status, data, retryAfter, sent }`: `status` is the HTTP status, 'TIMEOUT' or
 * 'NETWORK'; `data` is the API's plain-text message; `sent` says every byte left the browser, so the
 * server may have stored the file even though no answer arrived.
 *
 * @param {Object} params
 * @param {string} params.path - API path under the base URL, e.g. `/v2/.../attachments`
 * @param {File} params.file
 * @param {Function} [params.onProgress] - (fraction 0..1) => void, bytes sent so far
 * @param {number} [params.timeout]
 * @returns {Promise<Array>} DocumentDto rows
 */
export const uploadAttachmentFile = async ({ file, onProgress, path, timeout = UPLOAD_TIMEOUT_MS }) => {
    const headers = await prepareAuthHeaders(new Headers())
    const body = new FormData()
    body.append('files', file, file.name)

    let sent = false
    try {
        const { data } = await axios.post(`${API_BASE_URL}${path}`, body, {
            headers: Object.fromEntries(headers),
            timeout,
            onUploadProgress: ({ loaded, total }) => {
                const fraction = total ? loaded / total : 0
                if (fraction >= 1) sent = true
                onProgress?.(fraction)
            },
        })
        return data
    } catch (error) {
        const { response } = error
        let status = response?.status
        if (!status) status = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' ? 'TIMEOUT' : 'NETWORK'
        throw {
            status,
            data: typeof response?.data === 'string' && response.data ? response.data : undefined,
            retryAfter: Number(response?.headers?.['retry-after']) || undefined,
            sent,
        }
    }
}
